<template>
  <div data-app>
    <v-container fluid class="upload-history-container">
      <!-- Notification Snackbar -->
      <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        :timeout="snackbarTimeout"
        top
        right
      >
      <v-icon left>{{ snackbarIcon }}</v-icon>
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="closeNotification"
          :aria-label="t('uploadHistory.a11y.closeDialog')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Loading Skeleton -->
    <loading-skeleton v-if="initialLoading" />

    <!-- Error State -->
    <v-row v-else-if="listenerError">
      <v-col cols="12">
        <v-card class="pa-8 text-center" elevation="4">
          <v-avatar color="#ffebee" size="120" class="mb-4">
            <v-icon size="60" color="#c9242b">mdi-alert-circle</v-icon>
          </v-avatar>
          <h2 class="text-h5 font-weight-bold mb-3" style="color: #c9242b;">เกิดข้อผิดพลาด</h2>
          <p class="text-body-1 mb-4" style="color: #707070;">
            {{ listenerErrorMessage || 'ไม่สามารถโหลดประวัติการอัปโหลดได้' }}
          </p>
          <v-btn
            color="#6ec39b"
            dark
            large
            @click="retryLoadHistory"
            class="px-6"
          >
            <v-icon left>mdi-refresh</v-icon>
            ลองอีกครั้ง
          </v-btn>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content -->
    <v-row v-else>
      <v-col cols="12">
        <!-- Header Card -->
        <v-card class="mb-4 header-card" elevation="2">
          <v-card-title class="pa-6 d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="white"
                  @click="goBackToDocuments"
                  class="back-btn"
                  :aria-label="t('uploadHistory.a11y.backButton')"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
              </template>
              <span>{{ t('uploadHistory.back') }}</span>
            </v-tooltip>
            <div class="header-divider"></div>
            <div class="d-flex align-center flex-grow-1">
              <v-avatar color="#6ec39b" :size="UI_SIZES.HEADER_AVATAR" class="header-avatar-enhanced">
                <v-icon dark size="22">mdi-history</v-icon>
              </v-avatar>
              <div class="ml-4 header-text-container">
                <h1 class="header-title mb-1">{{ t('uploadHistory.title') }}</h1>
                <div class="header-subtitle">
                  {{ t('uploadHistory.subtitle') }}
                </div>
              </div>
            </div>
            <v-spacer></v-spacer>
            <!-- Real-time indicator -->
            <v-chip small color="#e8f5f0" text-color="#6ec39b" class="mr-2">
              <v-icon x-small class="mr-1 pulse-icon">mdi-access-point</v-icon>
              อัปเดตแบบ Real-time
            </v-chip>
          </v-card-title>
        </v-card>

        <!-- Statistics Cards -->
        <v-row v-if="statistics" class="mb-6" style="margin-top: 24px;">
          <v-col cols="12" sm="6" lg="3">
            <v-card class="stat-card stat-card-all" elevation="2" role="region" aria-label="Total uploads statistics">
              <div class="stat-overlay"></div>
              <v-card-text class="pa-4 position-relative">
                <div class="d-flex align-center justify-space-between">
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-medium mb-1 stat-label">
                      <v-icon small class="mr-1">mdi-file-multiple</v-icon>
                      {{ t('uploadHistory.stats.totalUploads') }}
                    </div>
                    <div class="text-h4 font-weight-bold stat-number">{{ statistics.totalUploads }}</div>
                    <div class="text-caption stat-sublabel">{{ t('uploadHistory.stats.items') }}</div>
                  </div>
                  <v-avatar color="#f5f5f5" size="48" class="stat-icon-wrapper ml-2">
                    <v-icon color="#707070" size="28">mdi-cloud-upload</v-icon>
                  </v-avatar>
                </div>
                <v-progress-linear
                  value="100"
                  height="3"
                  rounded
                  color="#707070"
                  class="mt-3"
                  background-opacity="0.1"
                  aria-hidden="true"
                ></v-progress-linear>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" lg="3">
            <v-card class="stat-card stat-card-success" elevation="2" role="region" aria-label="Successful uploads statistics">
              <div class="stat-overlay"></div>
              <v-card-text class="pa-4 position-relative">
                <div class="d-flex align-center justify-space-between">
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-medium mb-1 stat-label">
                      <v-icon small class="mr-1">mdi-check-circle</v-icon>
                      {{ t('uploadHistory.stats.successful') }}
                    </div>
                    <div class="text-h4 font-weight-bold stat-number">{{ statistics.successfulUploads }}</div>
                    <div class="text-caption stat-sublabel">
                      {{ successRate }}% {{ t('uploadHistory.stats.ofTotal') }}
                    </div>
                  </div>
                  <v-avatar color="#e8f5f0" size="48" class="stat-icon-wrapper ml-2">
                    <v-icon color="#6ec39b" size="28">mdi-check-bold</v-icon>
                  </v-avatar>
                </div>
                <v-progress-linear
                  :value="successRate"
                  height="3"
                  rounded
                  color="#6ec39b"
                  class="mt-3"
                  background-opacity="0.1"
                  :aria-label="`Success rate: ${successRate}%`"
                ></v-progress-linear>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" lg="3">
            <v-card class="stat-card stat-card-warning" elevation="2" role="region" aria-label="Partial success uploads statistics">
              <div class="stat-overlay"></div>
              <v-card-text class="pa-4 position-relative">
                <div class="d-flex align-center justify-space-between">
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-medium mb-1 stat-label">
                      <v-icon small class="mr-1">mdi-alert-circle</v-icon>
                      {{ t('uploadHistory.stats.partial') }}
                    </div>
                    <div class="text-h4 font-weight-bold stat-number">{{ statistics.partialUploads }}</div>
                    <div class="text-caption stat-sublabel">
                      {{ partialRate }}% {{ t('uploadHistory.stats.ofTotal') }}
                    </div>
                  </div>
                  <v-avatar color="#fff3e0" size="48" class="stat-icon-wrapper ml-2">
                    <v-icon color="#ff9800" size="28">mdi-alert-decagram</v-icon>
                  </v-avatar>
                </div>
                <v-progress-linear
                  :value="partialRate"
                  height="3"
                  rounded
                  color="#ff9800"
                  class="mt-3"
                  background-opacity="0.1"
                  :aria-label="`Partial rate: ${partialRate}%`"
                ></v-progress-linear>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" lg="3">
            <v-card class="stat-card stat-card-error" elevation="2" role="region" aria-label="Failed uploads statistics">
              <div class="stat-overlay"></div>
              <v-card-text class="pa-4 position-relative">
                <div class="d-flex align-center justify-space-between">
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-medium mb-1 stat-label">
                      <v-icon small class="mr-1">mdi-close-circle</v-icon>
                      {{ t('uploadHistory.stats.failed') }}
                    </div>
                    <div class="text-h4 font-weight-bold stat-number">{{ statistics.failedUploads }}</div>
                    <div class="text-caption stat-sublabel">
                      {{ failedRate }}% {{ t('uploadHistory.stats.ofTotal') }}
                    </div>
                  </div>
                  <v-avatar color="#ffebee" size="48" class="stat-icon-wrapper ml-2">
                    <v-icon color="#c9242b" size="28">mdi-close-thick</v-icon>
                  </v-avatar>
                </div>
                <v-progress-linear
                  :value="failedRate"
                  height="3"
                  rounded
                  color="#c9242b"
                  class="mt-3"
                  background-opacity="0.1"
                  :aria-label="`Failed rate: ${failedRate}%`"
                ></v-progress-linear>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Filter Card -->
        <v-card class="mb-6 filter-card" elevation="0" outlined>
          <v-card-title class="filter-card-title pb-3 pt-4 px-6">
            <div class="d-flex align-center w-100">
              <v-avatar color="#6ec39b" :size="UI_SIZES.HEADER_AVATAR" class="filter-header-avatar">
                <v-icon color="white" size="22">mdi-filter-variant</v-icon>
              </v-avatar>
              <div class="ml-4">
                <h2 class="filter-header-title">ค้นหาและกรองข้อมูล</h2>
              </div>
            </div>
          </v-card-title>
          <v-divider style="border-color: #e8f5f0; border-width: 2px;"></v-divider>
          <v-card-text class="pt-4 pb-4 px-6">
             <v-row>
               <v-col cols="12" md="4">
                 <v-text-field
                   v-model="search"
                   label="ค้นหา จาก ชื่อไฟล์, ผู้อัปโหลด, เลขที่เอกสาร"
                   placeholder="ค้นหา จาก ชื่อไฟล์, ผู้อัปโหลด, เลขที่เอกสาร"
                   append-icon="mdi-magnify"
                   outlined
                   hide-details
                   clearable
                   class="filter-docs-style"
                  @input="debounceInput"
                 ></v-text-field>

                 
               </v-col>
               <v-col cols="12" sm="6" md="3">
                 <v-select
                   v-model="filterStatus"
                   :items="statusList"
                   label="สถานะ"
                   placeholder="ทั้งหมด"
                   outlined
                   hide-details
                   clearable
                   class="filter-docs-style"
                 >
                   <template v-slot:no-data>
                     <v-list-item>
                       <v-list-item-title style="color: #999;">ไม่มีข้อมูล</v-list-item-title>
                     </v-list-item>
                   </template>
                 </v-select>
               </v-col>
               <v-col cols="12" sm="6" md="3">
                 <v-select
                   v-model="filterTemplate"
                   :items="templateList"
                   label="Template"
                   placeholder="ทั้งหมด"
                   outlined
                   hide-details
                   clearable
                   class="filter-docs-style"
                 >
                   <template v-slot:no-data>
                     <v-list-item>
                       <v-list-item-title style="color: #999;">ไม่มีข้อมูล</v-list-item-title>
                     </v-list-item>
                   </template>
                 </v-select>
               </v-col>
               <v-col cols="12" sm="12" md="2">
                 <v-menu
                   v-model="dateMenu"
                   :close-on-content-click="false"
                   :nudge-right="40"
                   transition="scale-transition"
                   offset-y
                   min-width="auto"
                 >
                   <template v-slot:activator="{ on, attrs }">
                     <v-text-field
                       :value="filterDate ? formatFilterDate(filterDate) : ''"
                       label="วันที่"
                       placeholder="ทั้งหมด"
                       readonly
                       outlined
                       hide-details
                       clearable
                       class="filter-docs-style"
                       @click:clear="clearDateFilter"
                       v-bind="attrs"
                       v-on="on"
                     ></v-text-field>
                   </template>
                  <v-date-picker
                    v-if="dateMenu"
                    v-model="filterDate"
                    @input="onDateSelected"
                    locale="th"
                    color="#6ec39b"
                    no-title
                    scrollable
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <!-- Active Filters Display -->
            <v-row v-if="hasFilters" class="mt-3">
              <v-col cols="12">
                <div class="d-flex align-center flex-wrap" role="region" aria-label="Active filters">
                  <span class="text-caption text--secondary mr-2">{{ t('uploadHistory.filter.filterBy') }}</span>

                  <v-chip
                    v-if="search"
                    small
                    close
                    color="#6ec39b"
                    text-color="white"
                    class="mr-2 mb-2"
                    @click:close="search = ''"
                    :aria-label="`${t('uploadHistory.a11y.clearFilter')}: ${search}`"
                  >
                    <v-icon small left>mdi-magnify</v-icon>
                    "{{ search }}"
                  </v-chip>

                  <v-chip
                    v-if="filterStatus"
                    small
                    close
                    color="#6ec39b"
                    text-color="white"
                    class="mr-2 mb-2"
                    @click:close="filterStatus = null"
                    :aria-label="`${t('uploadHistory.a11y.clearFilter')}: ${getStatusText(filterStatus)}`"
                  >
                    <v-icon small left>mdi-traffic-light</v-icon>
                    {{ getStatusText(filterStatus) }}
                  </v-chip>

                  <v-chip
                    v-if="filterTemplate"
                    small
                    close
                    color="#6ec39b"
                    text-color="white"
                    class="mr-2 mb-2"
                    @click:close="filterTemplate = null"
                    :aria-label="`${t('uploadHistory.a11y.clearFilter')}: ${getTemplateName(filterTemplate)}`"
                  >
                    <v-icon small left>mdi-file-document</v-icon>
                    {{ getTemplateName(filterTemplate) }}
                  </v-chip>

                  <v-chip
                    v-if="filterDate"
                    small
                    close
                    color="#6ec39b"
                    text-color="white"
                    class="mr-2 mb-2"
                    @click:close="filterDate = null"
                    :aria-label="`${t('uploadHistory.a11y.clearFilter')}: ${formatFilterDate(filterDate)}`"
                  >
                    <v-icon small left>mdi-calendar</v-icon>
                    {{ formatFilterDate(filterDate) }}
                  </v-chip>

                  <v-btn
                    x-small
                    text
                    color="error"
                    @click="clearAllFilters"
                    class="ml-2"
                    :aria-label="t('uploadHistory.filter.clearFilters')"
                  >
                    <v-icon small left>mdi-filter-off</v-icon>
                    {{ t('uploadHistory.clearAll') }}
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Data Table -->
        <v-card class="table-card" elevation="4">
          <v-card-title class="table-card-header">
            <div class="d-flex align-center w-100">
              <v-avatar color="#6ec39b" :size="UI_SIZES.HEADER_AVATAR" class="table-header-avatar">
                <v-icon color="white" size="22">mdi-table-large</v-icon>
              </v-avatar>
              <div class="ml-4 flex-grow-1">
                <h2 class="table-header-title">
                  {{ t('uploadHistory.table.title') }}
                </h2>
                <div class="table-header-subtitle">
                  {{ t('uploadHistory.table.showing') }}
                  <span class="table-count-badge">{{ filteredHistory.length }}</span>
                  {{ t('uploadHistory.table.items') }}
                </div>
              </div>
            </div>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table
              :headers="headers"
              :items="filteredHistory"
              :loading="loading"
              :items-per-page="PAGINATION.DEFAULT_ITEMS_PER_PAGE"
              :footer-props="{
                'items-per-page-options': PAGINATION.ITEMS_PER_PAGE_OPTIONS
              }"
              item-key="id"
              class="professional-table"
            >
              <!-- No Data -->
              <template v-slot:no-data>
                <div class="text-center py-12 empty-state">
                  <v-avatar color="grey lighten-3" size="120" class="mb-4">
                    <v-icon size="60" color="grey lighten-1">mdi-file-upload-outline</v-icon>
                  </v-avatar>
                  <div class="text-h5 font-weight-bold mb-2">{{ t('uploadHistory.empty.title') }}</div>
                  <div class="text-body-1 text--secondary mb-4">
                    {{ hasFilters ? t('uploadHistory.empty.noResults') : t('uploadHistory.empty.noData') }}
                  </div>
                  <v-btn
                    v-if="hasFilters"
                    color="primary"
                    outlined
                    @click="clearAllFilters"
                  >
                    <v-icon left>mdi-filter-off</v-icon>
                    {{ t('uploadHistory.filter.clearFilters') }}
                  </v-btn>
                </div>
              </template>

              <!-- Loading -->
              <template v-slot:loading>
                <div class="text-center py-12 loading-state">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="64"
                    width="6"
                    class="mb-4"
                  ></v-progress-circular>
                  <div class="text-h6 mb-2">{{ t('uploadHistory.loading.title') }}</div>
                  <div class="text-caption text--secondary">{{ t('uploadHistory.loading.subtitle') }}</div>
                </div>
              </template>

              <!-- Date/Time Column -->
              <template v-slot:[`item.uploadedAt`]="{ item }">
                <div class="py-2">
                  <div class="text-body-2 font-weight-bold primary--text mb-1">
                    <v-icon small color="primary" class="mr-1">mdi-calendar</v-icon>
                    {{ formatDate(item.uploadedAt) }}
                  </div>
                  <div class="text-body-2 text--secondary">
                    <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                    {{ formatTime(item.uploadedAt) }}
                  </div>
                </div>
              </template>

              <!-- Uploaded By Column -->
              <template v-slot:[`item.uploadedBy`]="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar :size="UI_SIZES.USER_AVATAR" color="primary lighten-4" class="mr-2">
                    <v-icon small color="primary">mdi-account</v-icon>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">{{ item.uploadedBy }}</span>
                </div>
              </template>

              <!-- File Column -->
              <template v-slot:[`item.fileInfo`]="{ item }">
                <div class="d-flex align-center py-2">
                  <v-icon class="mr-2" :color="getFileIconColor(item.fileInfo)">mdi-file-document-outline</v-icon>
                  <div>
                    <div
                      class="text-body-2 font-weight-medium text-truncate"
                      style="max-width: 200px"
                      :title="item.fileInfo?.name"
                    >
                      {{ item.fileInfo?.name || t('uploadHistory.file.notAvailable') }}
                    </div>
                    <div class="text-caption text--secondary">
                      {{ item.fileInfo?.sizeFormatted || t('uploadHistory.file.notAvailable') }}
                      <span v-if="item.fileInfo?.encoding" class="ml-1">
                        • {{ item.fileInfo.encoding }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Template Column -->
              <template v-slot:[`item.templateType`]="{ item }">
                <div class="d-flex align-center justify-center py-2">
                  <v-chip
                    small
                    :color="item.templateType === TEMPLATE_TYPES.TEMPLATE_1 ? 'indigo' : 'deep-purple'"
                    text-color="white"
                    class="font-weight-medium"
                  >
                    <v-icon small left>mdi-file-document</v-icon>
                    {{ getTemplateName(item.templateType) }}
                  </v-chip>
                </div>
              </template>

              <!-- Status Column -->
              <template v-slot:[`item.status`]="{ item }">
                <div class="d-flex align-center justify-center py-2">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    dark
                    class="font-weight-bold px-4 py-5 status-chip-large"
                    label
                  >
                    <v-icon left size="18">{{ getStatusIcon(item.status) }}</v-icon>
                    <span class="text-body-1">{{ getStatusText(item.status) }}</span>
                  </v-chip>
                </div>
              </template>

              <!-- Counts Column -->
              <template v-slot:[`item.counts`]="{ item }">
                <div class="counts-display py-2">
                  <div class="d-flex align-center justify-center mb-2">
                    <v-tooltip bottom v-if="item.successCount > 0">
                      <template v-slot:activator="{ on, attrs }">
                        <div class="count-box success-box mr-2" v-bind="attrs" v-on="on">
                          <v-icon small color="white" class="mr-1">mdi-check-bold</v-icon>
                          <span class="text-h6 font-weight-bold">{{ item.successCount || 0 }}</span>
                        </div>
                      </template>
                      <span>สำเร็จ {{ item.successCount }} รายการ</span>
                    </v-tooltip>
                    <div v-else class="count-box success-box mr-2">
                      <v-icon small color="white" class="mr-1">mdi-check-bold</v-icon>
                      <span class="text-h6 font-weight-bold">0</span>
                    </div>

                    <v-tooltip bottom v-if="item.failureCount > 0">
                      <template v-slot:activator="{ on, attrs }">
                        <div class="count-box error-box error-box-hover" v-bind="attrs" v-on="on">
                          <v-icon small color="white" class="mr-1">mdi-close-thick</v-icon>
                          <span class="text-h6 font-weight-bold">{{ item.failureCount }}</span>
                          <v-icon x-small color="white" class="ml-1">mdi-information-outline</v-icon>
                        </div>
                      </template>
                      <div style="max-width: 300px;">
                        <div class="font-weight-bold mb-1">ล้มเหลว {{ item.failureCount }} รายการ</div>
                        <div v-if="item.errors && item.errors.length > 0" class="text-caption">
                          <div v-for="(error, idx) in item.errors.slice(0, 3)" :key="idx" class="mb-1">
                            • แถว {{ error.row }}: {{ error.error }}
                          </div>
                          <div v-if="item.errors.length > 3" class="mt-1 font-italic">
                            และอีก {{ item.errors.length - 3 }} รายการ... (คลิก "ดูรายละเอียด" เพื่อดูทั้งหมด)
                          </div>
                        </div>
                      </div>
                    </v-tooltip>
                    <div v-else class="count-box error-box">
                      <v-icon small color="white" class="mr-1">mdi-close-thick</v-icon>
                      <span class="text-h6 font-weight-bold">0</span>
                    </div>
                  </div>
                  <div class="text-caption font-weight-bold grey--text text--darken-1">
                    {{ t('uploadHistory.counts.total') }} {{ item.totalRecords || 0 }} {{ t('uploadHistory.counts.items') }}
                  </div>
                </div>
              </template>

              <!-- Duration Column -->
              <template v-slot:[`item.duration`]="{ item }">
                <div class="duration-display py-2">
                  <div v-if="item.duration && item.duration > 0">
                    <v-icon :color="getDurationColor(item.duration)" class="mb-1">mdi-timer</v-icon>
                    <div class="text-h6 font-weight-bold" :style="{ color: getDurationColor(item.duration) }">
                      {{ item.duration.toFixed(2) }}
                    </div>
                    <div class="text-caption font-weight-bold grey--text text--darken-1">
                      {{ t('uploadHistory.duration.seconds') }}
                    </div>
                  </div>
                  <div v-else class="text-body-2 grey--text text--darken-1">
                    -
                  </div>
                </div>
              </template>

              <!-- Actions Column -->
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex align-center justify-center py-2">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        @click="viewDetails(item)"
                        @keydown.enter="viewDetails(item)"
                        v-bind="attrs"
                        v-on="on"
                        class="action-btn-view"
                        :aria-label="`${t('uploadHistory.a11y.viewDetailsButton')} ${item.fileInfo?.name || ''}`"
                      >
                        <v-icon small color="#6ec39b">mdi-eye-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ t('uploadHistory.viewDetails') }}</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Details Dialog -->
    <v-dialog v-model="detailDialog" max-width="900">
      <upload-log-viewer
        :log="selectedLog"
        @close="detailDialog = false"
      />
    </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { ref } from 'vue';
import UploadLogViewer from '@/components/UploadLogViewer.vue';
import LoadingSkeleton from '@/components/LoadingSkeleton.vue';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { useUploadHistory } from '@/composables/useUploadHistory';
import { useNotification } from '@/composables/useNotification';
import { useDebounceFn } from '@/composables/useDebounce';
import { useI18n } from '@/locales';
import {
  UPLOAD_STATUS,
  TEMPLATE_TYPES,
  PAGINATION,
  UI_SIZES,
  SEARCH_DEBOUNCE_MS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  TABLE_HEADERS,
} from '@/constants/uploadHistory';

export default {
  name: 'UploadHistory',

  components: {
    UploadLogViewer,
    LoadingSkeleton,
  },

  setup() {
    // Composables
    const { formatDate, formatTime, formatFilterDate, parseTimestamp } = useDateFormatter();
    const {
      getStatusText,
      getStatusColor,
      getStatusIcon,
      getFileIconColor,
      getDurationColor,
      getTemplateName,
      calculateStatistics,
      getSuccessRate: calculateSuccessRate,
      getPartialRate: calculatePartialRate,
      getFailedRate: calculateFailedRate,
      applyAllFilters,
      hasActiveFilters,
      getClearedFilters,
    } = useUploadHistory();
    const {
      snackbar,
      snackbarMessage,
      snackbarColor,
      snackbarTimeout,
      snackbarIcon,
      showSuccess,
      showError,
      showInfo,
      closeNotification,
      handleError,
    } = useNotification();
    const { t } = useI18n();

    // State
    const detailDialog = ref(false);
    const selectedLog = ref(null);
    const initialLoading = ref(true);
    const search = ref('');
    const filterStatus = ref(null);
    const filterTemplate = ref(null);
    const filterDate = ref(null);
    const dateMenu = ref(false);
    const listenerError = ref(false);
    const listenerErrorMessage = ref('');


    // Debounced search handler
    const debouncedSearch = useDebounceFn(() => {
      // Search is handled by computed property
    }, SEARCH_DEBOUNCE_MS);

    const onSearchInput = () => {
      debouncedSearch();
    };

    return {
      // State
      detailDialog,
      selectedLog,
      initialLoading,
      search,
      filterStatus,
      filterTemplate,
      filterDate,
      dateMenu,
      listenerError,
      listenerErrorMessage,

      // Notification
      snackbar,
      snackbarMessage,
      snackbarColor,
      snackbarTimeout,
      snackbarIcon,
      showSuccess,
      showError,
      showInfo,
      closeNotification,
      handleError,

      // Date formatting
      formatDate,
      formatTime,
      formatFilterDate,
      parseTimestamp,

      // Upload history helpers
      getStatusText,
      getStatusColor,
      getStatusIcon,
      getFileIconColor,
      getDurationColor,
      getTemplateName,
      calculateStatistics,
      calculateSuccessRate,
      calculatePartialRate,
      calculateFailedRate,
      applyAllFilters,
      hasActiveFilters,
      getClearedFilters,

      // I18n
      t,

      // Constants
      UPLOAD_STATUS,
      TEMPLATE_TYPES,
      PAGINATION,
      UI_SIZES,
      TABLE_HEADERS,

      // Methods
      onSearchInput,
    };
  },

  data() {
    return {
      headers: TABLE_HEADERS,
      statusList: [
        {text: 'ทั้งหมด', value: null},
        {text: 'สำเร็จ', value: 'completed'},
        {text: 'สำเร็จบางส่วน', value: 'completed_with_errors'},
        {text: 'ล้มเหลว', value: 'failed'},
        {text: 'กำลังประมวลผล', value: 'processing'},
      ],
      templateList: [
        {text: 'ทั้งหมด', value: null},
        {text: 'Template 1', value: 'template-1'},
        {text: 'Template 2', value: 'template-2'},
      ],
    };
  },

  computed: {
    ...mapState('uploadLog', ['uploadHistory', 'loading']),

    companyID() {
      return this.$route.params.companyId;
    },

    filteredHistory() {
      if (!this.uploadHistory) return [];

      const filters = {
        search: this.search,
        status: this.filterStatus,
        template: this.filterTemplate,
        date: this.filterDate,
      };

      return this.applyAllFilters(this.uploadHistory, filters, this.parseTimestamp);
    },

    statistics() {
      return this.calculateStatistics(this.filteredHistory);
    },

    successRate() {
      return this.calculateSuccessRate(this.statistics);
    },

    partialRate() {
      return this.calculatePartialRate(this.statistics);
    },

    failedRate() {
      return this.calculateFailedRate(this.statistics);
    },

    hasFilters() {
      return this.hasActiveFilters({
        search: this.search,
        status: this.filterStatus,
        template: this.filterTemplate,
        date: this.filterDate,
      });
    },
  },

  async mounted() {
    this.loadHistory();
    this.initialLoading = false;
  },

  beforeDestroy() {
    // ยกเลิก real-time listener เมื่อออกจากหน้า
    this.unsubscribeFromUploadHistory();
  },

  methods: {
    ...mapActions('uploadLog', ['subscribeToUploadHistory', 'unsubscribeFromUploadHistory', 'fetchUploadHistory']),

    loadHistory() {
      try {
        this.listenerError = false;
        this.listenerErrorMessage = '';

        // ใช้ real-time listener แทน
        this.subscribeToUploadHistory({
          companyID: this.companyID,
          limit: PAGINATION.DEFAULT_FETCH_LIMIT,
        });
        console.log('✅ Started real-time upload history listener');
      } catch (error) {
        console.error('❌ Failed to start listener:', error);
        this.listenerError = true;
        this.listenerErrorMessage = error.message || ERROR_MESSAGES.LOAD_HISTORY_FAILED;
        this.handleError(error, ERROR_MESSAGES.LOAD_HISTORY_FAILED);
      }
    },

    retryLoadHistory() {
      this.initialLoading = true;
      this.loadHistory();
      setTimeout(() => {
        this.initialLoading = false;
      }, 500);
    },

    viewDetails(log) {
      this.selectedLog = log;
      this.detailDialog = true;
    },

    clearAllFilters() {
      const cleared = this.getClearedFilters();
      this.search = cleared.search;
      this.filterStatus = cleared.status;
      this.filterTemplate = cleared.template;
      this.filterDate = cleared.date;
      this.showInfo(SUCCESS_MESSAGES.FILTERS_CLEARED);
    },

    clearDateFilter() {
      this.filterDate = null;
      this.dateMenu = false;
    },

    onDateSelected(date) {
      this.filterDate = date;
      this.dateMenu = false;
    },

    goBackToDocuments() {
      this.$router.push({
        name: 'Company.Documents',
        params: {
          companyId: this.companyID,
        },
      });
    },
  },
};
</script>

<style scoped>
.upload-history-container {
  background: #f5f5f5;
  min-height: 100vh;
  padding: 24px;
}

.upload-history-container > .v-row {
  position: relative;
}

/* Header Card - Clean Professional */
.header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8faf9 100%);
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-radius: 12px !important;
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

.header-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #6ec39b 0%, #5ab088 100%);
}

.header-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.header-card .back-btn {
  transition: all 0.3s ease;
  background-color: rgba(110, 195, 155, 0.08) !important;
}

.header-card .back-btn:hover {
  transform: translateX(-4px);
  background-color: rgba(110, 195, 155, 0.15) !important;
}

.header-card .refresh-btn {
  border: 1px solid #6ec39b !important;
  color: #6ec39b !important;
  transition: all 0.3s ease;
}

.header-card .refresh-btn:hover {
  background: #6ec39b !important;
  color: white !important;
}

/* Header Avatar Enhancement */
.header-avatar-enhanced {
  box-shadow: 0 4px 12px rgba(110, 195, 155, 0.25);
  transition: all 0.3s ease;
  border: 3px solid rgba(255, 255, 255, 0.9);
}

.header-card:hover .header-avatar-enhanced {
  box-shadow: 0 6px 16px rgba(110, 195, 155, 0.35);
  transform: scale(1.05);
}

/* Header Divider */
.header-divider {
  width: 2px;
  height: 48px;
  background: linear-gradient(180deg, transparent 0%, #e0e0e0 20%, #e0e0e0 80%, transparent 100%);
  margin: 0 24px;
  flex-shrink: 0;
}

/* Header Text Container */
.header-text-container {
  flex: 1;
  padding: 4px 0;
}

.header-title {
  font-size: 1.75rem !important;
  font-weight: 700 !important;
  color: #2c3e50 !important;
  line-height: 1.3 !important;
  letter-spacing: -0.02em !important;
  margin: 0 !important;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-subtitle {
  font-size: 0.95rem !important;
  font-weight: 500 !important;
  color: #6c757d !important;
  line-height: 1.5 !important;
  letter-spacing: 0.01em !important;
  display: flex;
  align-items: center;
  margin-top: 2px;
}

/* Statistics Cards - Clean Professional */
.stat-card {
  border-radius: 8px !important;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0 !important;
  overflow: hidden;
  position: relative;
  background: white;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.stat-overlay {
  display: none;
}

/* Card Specific Colors - Clean & Subtle */
.stat-card-all {
  background: white !important;
  border-left: 4px solid #707070 !important;
}

.stat-card-success {
  background: white !important;
  border-left: 4px solid #6ec39b !important;
}

.stat-card-warning {
  background: white !important;
  border-left: 4px solid #ff9800 !important;
}

.stat-card-error {
  background: white !important;
  border-left: 4px solid #c9242b !important;
}

.stat-card-all .stat-label,
.stat-card-all .stat-number {
  color: #707070 !important;
}

.stat-card-success .stat-label,
.stat-card-success .stat-number {
  color: #6ec39b !important;
}

.stat-card-warning .stat-label,
.stat-card-warning .stat-number {
  color: #ff9800 !important;
}

.stat-card-error .stat-label,
.stat-card-error .stat-number {
  color: #c9242b !important;
}

.stat-label,
.stat-number,
.stat-sublabel {
  text-shadow: none;
}

.stat-label .v-icon {
  color: inherit !important;
}

.stat-icon-wrapper {
  box-shadow: none;
}

.stat-number {
  font-size: 2rem !important;
  line-height: 1.2;
  font-weight: 700 !important;
}

.stat-sublabel {
  color: #707070 !important;
}

/* Filter Card - Clean Professional */
.filter-card {
  border-radius: 12px !important;
  border: 1px solid #e0e0e0;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  margin-top: 24px;
}

.filter-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.filter-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%) !important;
  border-bottom: 1px solid #e0e0e0;
}

/* Filter Header Avatar */
.filter-header-avatar {
  box-shadow: 0 2px 8px rgba(110, 195, 155, 0.3);
  transition: all 0.3s ease;
}

.filter-card:hover .filter-header-avatar {
  box-shadow: 0 4px 12px rgba(110, 195, 155, 0.4);
  transform: scale(1.05);
}

/* Filter Header Title */
.filter-header-title {
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  color: #2c3e50 !important;
  line-height: 1.3 !important;
  letter-spacing: -0.01em !important;
  margin: 0 !important;
}

.search-field {
  border-radius: 4px;
}

/* Table Card - Clean Professional */
.table-card {
  border-radius: 12px !important;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  margin-top: 24px;
}

.table-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.table-card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%) !important;
  color: #333 !important;
  padding: 20px 28px !important;
  border-bottom: 2px solid #e8f5f0;
}

/* Table Header Avatar */
.table-header-avatar {
  box-shadow: 0 2px 8px rgba(110, 195, 155, 0.3);
  transition: all 0.3s ease;
}

.table-card:hover .table-header-avatar {
  box-shadow: 0 4px 12px rgba(110, 195, 155, 0.4);
  transform: scale(1.05);
}

/* Table Header Title */
.table-header-title {
  font-size: 1.25rem !important;
  font-weight: 700 !important;
  color: #2c3e50 !important;
  line-height: 1.3 !important;
  letter-spacing: -0.01em !important;
  margin: 0 !important;
  margin-bottom: 4px !important;
}

/* Table Header Subtitle */
.table-header-subtitle {
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  color: #6c757d !important;
  line-height: 1.4 !important;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Table Count Badge */
.table-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #6ec39b 0%, #5ab088 100%);
  color: white;
  font-weight: 700;
  font-size: 0.875rem;
  padding: 2px 10px;
  border-radius: 12px;
  min-width: 32px;
  box-shadow: 0 2px 6px rgba(110, 195, 155, 0.3);
  transition: all 0.3s ease;
}

.table-count-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 3px 8px rgba(110, 195, 155, 0.4);
}

/* Professional Table */
.professional-table {
  border-radius: 0 !important;
}

.professional-table >>> .v-data-table__wrapper {
  border-radius: 0;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
}

.professional-table >>> thead {
  background: #f5f7fa !important;
}

.professional-table >>> thead th {
  font-weight: 700 !important;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 1px;
  color: #707070 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  padding: 18px 16px !important;
  white-space: nowrap;
}

.professional-table >>> tbody tr {
  transition: all 0.3s ease;
  position: relative;
}

.professional-table >>> tbody tr:nth-child(odd) {
  background-color: #fafafa;
}

.professional-table >>> tbody tr:nth-child(even) {
  background-color: white;
}

.professional-table >>> tbody tr:hover {
  background: #f5f5f5 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.professional-table >>> tbody td {
  padding: 24px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  vertical-align: middle !important;
  height: 100px !important;
  min-height: 100px !important;
}

.professional-table >>> tbody tr {
  margin-bottom: 8px;
}

/* Action Button */
.action-btn {
  transition: all 0.3s ease;
  background: #6ec39b !important;
}

.action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(110, 195, 155, 0.4) !important;
}

.action-btn .v-icon {
  color: white !important;
}

.action-btn-view {
  transition: all 0.3s ease;
  background: rgba(110, 195, 155, 0.1) !important;
}

.action-btn-view:hover {
  background: rgba(110, 195, 155, 0.2) !important;
  transform: scale(1.15);
}

/* Chips */
.v-chip {
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Status Chip */
.status-chip-large {
  min-width: 150px !important;
  height: 44px !important;
  font-size: 1rem !important;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s ease;
}

.status-chip-large:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2) !important;
}

/* Count Display */
.counts-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  width: 100%;
}

.count-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-radius: 8px;
  color: white;
  min-width: 65px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.success-box {
  background: #3a7d2d;
}

.error-box {
  background: #c9242b;
}

.error-box-hover {
  cursor: help !important;
  position: relative;
}

.error-box-hover::after {
  content: '';
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #fff;
  border-radius: 50%;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.3);
  }
}

.count-box:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

.error-box-hover:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 8px 24px rgba(201, 36, 43, 0.4) !important;
}

.count-box .text-h6 {
  font-size: 1.25rem !important;
  line-height: 1;
  font-weight: 800 !important;
}

/* Duration Display */
.duration-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  width: 100%;
}

.duration-display .v-icon {
  font-size: 28px !important;
  margin-bottom: 4px;
}

.duration-display .text-h6 {
  font-size: 1.5rem !important;
  line-height: 1;
  font-weight: 800 !important;
}

/* Empty & Loading States */
.v-data-table__empty-wrapper {
  padding: 48px 0 !important;
}

.empty-state,
.loading-state {
  padding: 60px 24px !important;
}

/* Simple fade-in animation */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.v-card {
  animation: fadeIn 0.3s ease-out;
}

/* Real-time pulse animation */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

.pulse-icon {
  animation: pulse 2s ease-in-out infinite;
}

/* Responsive Design */
@media (max-width: 1264px) {
  .professional-table >>> thead th:nth-child(4),
  .professional-table >>> tbody td:nth-child(4),
  .professional-table >>> thead th:nth-child(7),
  .professional-table >>> tbody td:nth-child(7) {
    display: none !important;
  }
}

@media (max-width: 960px) {
  .professional-table >>> thead th:nth-child(2),
  .professional-table >>> tbody td:nth-child(2),
  .professional-table >>> thead th:nth-child(4),
  .professional-table >>> tbody td:nth-child(4),
  .professional-table >>> thead th:nth-child(7),
  .professional-table >>> tbody td:nth-child(7) {
    display: none !important;
  }

  .professional-table >>> tbody td {
    padding: 12px 8px !important;
    height: auto !important;
    min-height: 60px !important;
  }

  .status-chip-large {
    min-width: 100px !important;
    height: 36px !important;
    font-size: 0.85rem !important;
  }
}

@media (max-width: 600px) {
  .upload-history-container {
    padding: 16px 12px;
  }

  .stat-number {
    font-size: 1.8rem !important;
  }

  .status-chip-large {
    min-width: 90px !important;
    height: 32px !important;
    font-size: 0.75rem !important;
  }
}

/* Text Utilities */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Filter Card Enhancements */
.filter-card {
  border: 1px solid #e0e0e0 !important;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

/* Documents style filter inputs */
.filter-docs-style .v-input__slot {
  border-radius: 4px !important;
}

.filter-docs-style:hover:not(.v-input--is-focused) .v-input__slot {
  border-color: #999 !important;
}

.filter-docs-style.v-input--is-focused .v-input__slot {
  border-color: #1976d2 !important;
  border-width: 2px !important;
}

/* Label */
.filter-docs-style .v-label {
  color: rgba(0, 0, 0, 0.6) !important;
  font-size: 16px !important;
}

.filter-docs-style .v-label--active {
  color: #1976d2 !important;
  font-size: 12px !important;
}

/* Input and selection text */
.filter-docs-style .v-text-field__slot input,
.filter-docs-style .v-select__slot input {
  font-size: 16px !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.filter-docs-style .v-select__selection {
  font-size: 16px !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

/* Placeholder */
.filter-docs-style input::placeholder {
  color: rgba(0, 0, 0, 0.38) !important;
  font-size: 16px !important;
}

/* Append icon (search icon on right) */
.filter-docs-style .v-input__append-inner .v-icon {
  color: rgba(0, 0, 0, 0.54) !important;
}

/* Clear button */
.filter-docs-style .v-input__icon--clear .v-icon {
  font-size: 20px !important;
  color: rgba(0, 0, 0, 0.54) !important;
}

.filter-docs-style .v-input__icon--clear .v-icon:hover {
  color: #f44336 !important;
}

/* Wrapper Styles - Prevent layout issues */
[data-app] {
  width: 100%;
  min-height: 100vh;
}
</style>
